import { useCopilotAction } from '@copilotkit/react-core';

import { Stream, STREAM_TYPES, STREAM_STATES, STREAM_SOURCES } from 'types/stream';

import { useAPI } from 'hooks/api/useAPI';

// Helper to safely parse JSON or return the value if already parsed
function safeJsonParse<T>(value: string | T | undefined): T | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    // If it's a string that's not valid JSON, treat it as a single-item array
    return [value] as unknown as T;
  }
}

export function useListStreamsTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listStreams',
    description:
      'List video streams in the system. Use this to find stream IDs for filtering analytics or file searches. Streams are video sources that can come from cameras, files, or other sources.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of stream IDs to fetch',
        required: false,
      },
      {
        name: 'statuses',
        type: 'string',
        description:
          `JSON array of statuses to filter by: ${STREAM_STATES.join(', ')}`,
        required: false,
      },
      {
        name: 'names',
        type: 'string',
        description: 'JSON array of stream names to search for',
        required: false,
      },
      {
        name: 'cameraIds',
        type: 'string',
        description: 'JSON array of camera IDs to filter streams by',
        required: false,
      },
      {
        name: 'gatewayIds',
        type: 'string',
        description: 'JSON array of gateway IDs to filter streams by',
        required: false,
      },
      {
        name: 'streamTypes',
        type: 'string',
        description:
          `JSON array of stream types: ${STREAM_TYPES.join(', ')}. rtsp is used for ALL live RTSP/HTTP streams, webrtc is used for live WebRTC streams, and file is used for HTTP static file streams.`,
        required: false,
      },
      {
        name: 'streamSources',
        type: 'string',
        description:
          `JSON array of stream sources: ${STREAM_SOURCES.join(', ')}. uri_stream is used for user defined source RTSP/HTTP live streams, camera_stream is used for RTSP/HTTP source streams belonging to a camera, and pipeline_stream is used for streams output by a pipeline/workflow.`,
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      statuses?: string | string[];
      names?: string | string[];
      cameraIds?: string | string[];
      gatewayIds?: string | string[];
      streamTypes?: string | string[];
      streamSources?: string | string[];
      limit?: number;
    }) => {
      try {
        const ids = safeJsonParse<string[]>(args.ids);
        const statuses = safeJsonParse<string[]>(args.statuses);
        const names = safeJsonParse<string[]>(args.names);
        const cameraIds = safeJsonParse<string[]>(args.cameraIds);
        const gatewayIds = safeJsonParse<string[]>(args.gatewayIds);
        const streamTypes = safeJsonParse<string[]>(args.streamTypes);
        const streamSources = safeJsonParse<string[]>(args.streamSources);
        const { data } = await client.get(
          `/internal/apps/${applicationID}/streams`,
          {
            params: {
              pagination: 'offset',
              page: 1,
              limit: args.limit || 50,
              stream_ids: ids,
              status: statuses,
              stream_names: names,
              camera_ids: cameraIds,
              gateway_ids: gatewayIds,
              stream_types: streamTypes,
              sources: streamSources,
            },
          }
        );

        const streams = data?.data || [];

        if (streams.length === 0) {
          return {
            message: 'No streams found matching your criteria.',
            streams: [],
          };
        }

        return {
          message: `Found ${streams.length} stream(s)`,
          streams: streams.map((stream: Stream) => stream),
        };
      } catch (error) {
        return {
          message: 'Failed to load streams',
          error: error instanceof Error ? error.message : 'Unknown error',
          streams: [],
        };
      }
    },
  });
}
